/**
 * The colour key for the canvas.
 *
 * The timeline says a lot with colour and nothing with words: an amber dashed block and a teal
 * solid one are different claims about how much to trust a boundary, and until now the only way
 * to learn that was to hover every block in turn. This is the one place that spells it out.
 *
 * The swatches are built from `theme`, the same object `draw.ts` paints with, rather than
 * restated by hand. A legend whose colours are typed a second time is a legend that goes stale
 * the first time the palette moves, and it would go stale silently -- it would still look like
 * a legend. Deriving them means the swatch is wrong only if the block is wrong too.
 */

import type { TFunction } from "i18next";
import { theme } from "./theme";

export interface LegendSwatch {
  background: string;
  /** The void's stripes. Absent for everything else, which is a flat fill. */
  backgroundImage?: string;
  borderColor: string;
  /** Dashed exactly where the canvas dashes: a boundary that is not settled yet. */
  borderStyle: "solid" | "dashed";
}

export interface LegendItem {
  key: string;
  /** One or two words. The row has to fit beside the zone label. */
  label: string;
  /** The claim behind the colour, carried as a title for anyone who wants the long form. */
  hint: string;
  swatch: LegendSwatch;
}

/**
 * The void's hatch, scaled for a swatch.
 *
 * The canvas rules a 1 px line every 7 px across a band tens of pixels tall. At swatch size
 * that is one stripe and reads as a smudge, so the period is tightened until the pattern is
 * legible. The angle is not adjusted: `draw.ts` runs its lines from the bottom edge up to the
 * right, and a legend that mirrored the hatch would be a different marking.
 */
const HATCH = `repeating-linear-gradient(135deg, ${theme.voidHatch} 0 1px, transparent 1px 4px)`;

/**
 * What the key lists, in the order the canvas stacks it: the show band, then the track band,
 * then the three markings that are neither -- what has no audio, what has not been published
 * yet, and what is outside the record -- and last the lane that can be switched off.
 *
 * Fixed, not derived from what is currently on screen. A legend that gained and lost rows as
 * you panned would reflow the row on every pan and would also be answering the wrong question:
 * the reader is asking what a colour means, not whether one happens to be visible. The chat
 * lane is the exception because it can be switched off entirely, and then the colour is not
 * part of this timeline's vocabulary at all. The listened tracker is the second exception,
 * for the same reason: signed out it is never drawn, so naming its two colours would point
 * at a marking that does not exist.
 *
 * Starred tracks are deliberately absent. That is a 3 px stripe inside a block rather than a
 * block of its own, a swatch would misrepresent its shape, and the star badge in the list and
 * the queue already carries its own title.
 */
export function legendItems({
  chat,
  listened,
  t,
}: {
  chat: boolean;
  listened: boolean;
  t: TFunction;
}): readonly LegendItem[] {
  const items: LegendItem[] = [
    {
      key: "show",
      label: t("legend.show.label"),
      hint: t("legend.show.hint"),
      swatch: {
        background: theme.showFill,
        borderColor: theme.showEdge,
        borderStyle: "solid",
      },
    },
    {
      key: "provisional",
      label: t("legend.provisional.label"),
      hint: t("legend.provisional.hint"),
      swatch: {
        background: theme.provisionalFill,
        borderColor: theme.provisionalEdge,
        borderStyle: "dashed",
      },
    },
    {
      key: "live",
      label: t("legend.live.label"),
      hint: t("legend.live.hint"),
      swatch: {
        background: theme.liveFill,
        borderColor: theme.liveEdge,
        borderStyle: "dashed",
      },
    },
    {
      key: "track",
      label: t("legend.track.label"),
      hint: t("legend.track.hint"),
      swatch: {
        background: theme.trackFill,
        borderColor: theme.trackEdge,
        borderStyle: "solid",
      },
    },
    {
      key: "tombstone",
      label: t("legend.tombstone.label"),
      hint: t("legend.tombstone.hint"),
      swatch: {
        background: theme.tombstoneFill,
        borderColor: theme.tombstoneEdge,
        borderStyle: "solid",
      },
    },
    {
      key: "archived",
      label: t("legend.archived.label"),
      hint: t("legend.archived.hint"),
      swatch: {
        background: theme.archivedFill,
        borderColor: theme.archivedEdge,
        borderStyle: "solid",
      },
    },
    {
      key: "gap",
      label: t("legend.gap.label"),
      hint: t("legend.gap.hint"),
      swatch: {
        background: theme.gapFill,
        borderColor: theme.gapFill,
        borderStyle: "solid",
      },
    },
    {
      key: "live-delay",
      label: t("legend.live-delay.label"),
      hint: t("legend.live-delay.hint"),
      swatch: {
        background: theme.liveDelayFill,
        borderColor: theme.liveDelayEdge,
        borderStyle: "solid",
      },
    },
    {
      key: "void",
      label: t("legend.void.label"),
      hint: t("legend.void.hint"),
      swatch: {
        background: theme.background,
        backgroundImage: HATCH,
        borderColor: theme.voidHatch,
        borderStyle: "solid",
      },
    },
  ];

  if (chat) {
    items.push({
      key: "chat",
      label: t("legend.chat.label"),
      hint: t("legend.chat.hint"),
      swatch: {
        background: theme.chatFill,
        borderColor: theme.chatFill,
        borderStyle: "solid",
      },
    });
  }

  if (listened) {
    items.push(
      {
        key: "unheard",
        label: t("legend.unheard.label"),
        hint: t("legend.unheard.hint"),
        swatch: {
          background: theme.unheardFill,
          borderColor: theme.unheardFill,
          borderStyle: "solid",
        },
      },
      {
        key: "heard",
        label: t("legend.heard.label"),
        hint: t("legend.heard.hint"),
        swatch: {
          background: theme.heardFill,
          borderColor: theme.heardFill,
          borderStyle: "solid",
        },
      },
    );
  }

  return items;
}
